
import { QuixFeed } from "@/components/quix/QuixFeed";
import { QuixUploadDialog } from "@/components/quix/QuixUploadDialog";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

const Quix = () => {
  const [videos, setVideos] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 5;
  const { user } = useAuth();

  // Function to calculate video score for algorithmic sorting
  const calculateVideoScore = (video: any, userLikes: any[]) => {
    let score = 0;
    
    // Base score from creation time (newer videos get higher scores)
    const ageInHours = (Date.now() - new Date(video.created_at).getTime()) / (1000 * 60 * 60);
    const freshnessFactor = Math.max(0, 1 - (ageInHours / 72)); // Videos up to 3 days old
    score += freshnessFactor * 50; // Freshness is worth up to 50 points
    
    // Points from engagement metrics
    score += (video.likes_count || 0) * 2;
    score += (video.comments_count || 0) * 3;
    score += (video.shares_count || 0) * 5;
    
    // Bonus if the user liked similar content
    if (user && userLikes.length > 0) {
      // Check if this video is from the same creator as videos the user liked
      const creatorMatch = userLikes.some(like => 
        like.video?.user_id === video.user_id
      );
      
      if (creatorMatch) {
        score += 30; // Significant boost for content from creators the user likes
      }
      
      // Check for similar descriptions (simplistic approach)
      const userLikedDescriptions = userLikes
        .filter(like => like.video?.description)
        .map(like => like.video.description.toLowerCase());
      
      if (video.description) {
        const videoTags = video.description.toLowerCase().split('#').slice(1);
        
        // Check if any tags from this video appear in descriptions of videos the user liked
        const tagMatches = videoTags.some(tag => 
          userLikedDescriptions.some(desc => desc.includes(tag))
        );
        
        if (tagMatches) {
          score += 20; // Boost for content with similar tags/topics
        }
      }
    }
    
    return score;
  };

  const fetchUserLikes = async () => {
    if (!user) return [];
    
    try {
      const { data, error } = await supabase
        .from('quix_reactions')
        .select(`
          *,
          video:video_id (
            user_id,
            description
          )
        `)
        .eq('user_id', user.id)
        .eq('reaction_type', 'like');
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching user likes:", error);
      return [];
    }
  };

  const fetchVideos = async () => {
    console.log('Fetching videos for page:', page);
    setLoading(true);
    
    try {
      // First get all videos
      const { data, error } = await supabase
        .from("quix_videos")
        .select(`
          *,
          profiles (
            username,
            avatar_url
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (data) {
        // Log the fetched data
        console.log("Fetched videos:", data);
        
        // Process videos to include full URLs
        const processedData = data.map(video => {
          // Get the public URL for the video
          const { data: { publicUrl } } = supabase.storage
            .from('quix-videos')
            .getPublicUrl(video.url);

          return {
            ...video,
            url: publicUrl,
            likes_count: video.likes_count || 0,
            comments_count: video.comments_count || 0,
            shares_count: video.shares_count || 0,
            bookmarks_count: video.bookmarks_count || 0
          };
        });
        
        // Get user likes for algorithmic sorting
        const userLikes = await fetchUserLikes();
        
        // Sort videos based on the algorithmic score
        const sortedVideos = processedData.sort((a, b) => {
          const scoreA = calculateVideoScore(a, userLikes);
          const scoreB = calculateVideoScore(b, userLikes);
          return scoreB - scoreA;
        });
        
        // Paginate the sorted results
        const paginatedVideos = sortedVideos.slice(
          page * PAGE_SIZE, 
          (page + 1) * PAGE_SIZE
        );
        
        console.log("Processed videos with URLs and algorithmic ranking:", paginatedVideos);
        
        if (page === 0) {
          setVideos(paginatedVideos);
        } else {
          setVideos(prev => [...prev, ...paginatedVideos]);
        }
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast({
        title: "Error",
        description: "Failed to load videos. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [page, user]); // Re-fetch when user changes to update algorithmic feed

  const handleVideoUploaded = () => {
    console.log("Video uploaded, refreshing feed...");
    setPage(0); // Reset to first page
    fetchVideos(); // Fetch videos again
  };

  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  return (
    <div className="fixed inset-0 bg-black">
      <div className="absolute top-4 right-4 z-50">
        <QuixUploadDialog onUploadComplete={handleVideoUploaded} />
      </div>
      <QuixFeed videos={videos} onLoadMore={loadMore} isLoading={loading} />
    </div>
  );
};

export default Quix;
